import type { RouterConfig } from '@nuxt/schema'

import type { RouteLocationNormalizedLoaded } from 'vue-router'

import { getPreviousRoute, getRouteScrollNavigationId } from './utils/route-scroll'

const ARTICLE_LIST_PATTERN = /^\/articles\/[^/]+\/?$/
const MEDIA_LIST_PATTERN = /^\/media\/[^/]+\/?$/
/** 嵌套异步列表页需等组件报告数据渲染完成后再回顶。 */
const DATA_READY_ROUTE_PATTERNS = [
  ARTICLE_LIST_PATTERN,
  MEDIA_LIST_PATTERN,
  /^\/company\/qualifications\/?$/,
  /^\/mall\/?$/,
]
/** 接口异常或组件未挂载时避免路由滚动永久等待。 */
const DATA_READY_SCROLL_TIMEOUT = 15_000

type RouteScrollPosition = {
  behavior?: ScrollOptions['behavior']
  el?: Element | string
  left?: number
  top?: number
}

/** 获取锚点目标需要补充的顶部滚动距离。 */
function getHashScrollMarginTop(selector: string) {
  try {
    const element = document.querySelector(selector)
    if (!element) return 0
    return (
      (Number.parseFloat(getComputedStyle(element).scrollMarginTop) || 0) +
      (Number.parseFloat(getComputedStyle(document.documentElement).scrollPaddingTop) || 0)
    )
  } catch {
    return 0
  }
}

/** 判断页面配置是否禁止路由回顶。 */
function shouldScrollToTop(to: RouteLocationNormalizedLoaded, from: RouteLocationNormalizedLoaded) {
  if (typeof to.meta.scrollToTop === 'function') return to.meta.scrollToTop(to, from)
  return to.meta.scrollToTop
}

/** 判断目标路由是否需要等待列表数据完成渲染。 */
function shouldWaitForData(path: string) {
  return DATA_READY_ROUTE_PATTERNS.some((pattern) => pattern.test(path))
}

/** 判断目标路由的列表组件是否已经完成本次数据渲染。 */
function isRouteDataReady(path: string, navigationId: number) {
  const normalizedPath = path.replace(/\/$/, '') || '/'
  return [...document.querySelectorAll<HTMLElement>('[data-route-scroll-path]')].some(
    (element) =>
      (element.dataset.routeScrollPath?.replace(/\/$/, '') || '/') === normalizedPath &&
      element.dataset.routeScrollNavigationId === String(navigationId) &&
      element.dataset.routeScrollReady === 'true'
  )
}

/** 等待页面异步数据和 DOM 完成后再执行路由滚动。 */
function deferUntilPageReady(
  to: RouteLocationNormalizedLoaded,
  getPosition: () => RouteScrollPosition
) {
  const nuxtApp = useNuxtApp()
  const router = useRouter()

  return new Promise<false | RouteScrollPosition>((resolve) => {
    let startedAt = 0
    const applyPosition = () => {
      window.requestAnimationFrame(() => {
        if (router.currentRoute.value.fullPath !== to.fullPath) {
          resolve(false)
          return
        }

        if (
          shouldWaitForData(to.path) &&
          !isRouteDataReady(to.path, getRouteScrollNavigationId()) &&
          performance.now() - startedAt < DATA_READY_SCROLL_TIMEOUT
        ) {
          applyPosition()
          return
        }
        resolve(getPosition())
      })
    }

    const startPolling = () => {
      startedAt = performance.now()
      applyPosition()
    }

    nuxtApp.hooks.hookOnce('page:loading:end', () => {
      const transitionPromise = (
        nuxtApp as typeof nuxtApp & { '~transitionPromise'?: Promise<unknown> }
      )['~transitionPromise']
      if (transitionPromise) {
        void transitionPromise.then(startPolling, startPolling)
        return
      }
      startPolling()
    })
  })
}

export default {
  scrollBehavior(to, from, savedPosition) {
    if (typeof window === 'undefined') return false

    const previousRoute = getPreviousRoute() ?? from
    if (import.meta.dev) {
      ;(window as typeof window & { __routeScrollDebug?: unknown }).__routeScrollDebug = {
        to: to.path,
        previous: previousRoute.path,
        savedPosition,
      }
    }
    if (savedPosition) return savedPosition

    if (to.path === previousRoute.path) {
      if (previousRoute.hash && !to.hash) return savedPosition ?? { left: 0, top: 0 }
      if (to.hash) {
        return {
          el: to.hash,
          top: getHashScrollMarginTop(to.hash),
          behavior: 'auto',
        }
      }
      return false
    }

    const isArticleList = ARTICLE_LIST_PATTERN.test(to.path)
    const isFromArticleList = ARTICLE_LIST_PATTERN.test(previousRoute.path)
    const isMediaList = MEDIA_LIST_PATTERN.test(to.path)
    const isFromMediaList = MEDIA_LIST_PATTERN.test(previousRoute.path)
    if (shouldScrollToTop(to, previousRoute) === false) return false

    // 同一文章列表内的分类切换由列表组件在新数据渲染完成后负责滚动。
    if (isArticleList && isFromArticleList && !to.hash) return false
    if (isMediaList && isFromMediaList && !to.hash) return false

    return deferUntilPageReady(to, () => {
      if (to.hash) {
        return {
          el: to.hash,
          top: getHashScrollMarginTop(to.hash),
          behavior: 'auto',
        }
      }

      if (isArticleList && !isFromArticleList) {
        const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches
        return { left: 0, top: 0, behavior: reduceMotion ? 'auto' : 'smooth' }
      }

      if (isMediaList && !isFromMediaList) {
        const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches
        return { left: 0, top: 0, behavior: reduceMotion ? 'auto' : 'smooth' }
      }

      return { left: 0, top: 0, behavior: 'auto' }
    })
  },
} satisfies RouterConfig
